/**
 * In-app search index.
 *
 * Flat list of searchable "destinations" across the site: pages, product
 * categories, individual product models, finishes, FAQs. Each entry has a
 * clean title, an optional subtitle (used for keyword matching), a kind label
 * for grouping in the UI, and an href the overlay navigates to on click.
 *
 * Keep this hand-curated — it stays small, is zero-dependency, and the index
 * doesn't need to be in sync with a backend search engine. New products can
 * be added here when they ship.
 */

const PAGES = [
  { title: "Home", subtitle: "Eurosteel · architectural steel windows & doors", kind: "Page", href: "/" },
  { title: "All Products", subtitle: "Steel windows, doors, luxury entrances, hardware", kind: "Page", href: "/products" },
  { title: "Door Configurator", subtitle: "Build your door in 3D and request a quote", kind: "Page", href: "/door-configurator" },
  { title: "Technical", subtitle: "Profiles, glazing, divider bars, mounting, downloads", kind: "Page", href: "/technical" },
  { title: "Finishes", subtitle: "Dulux Powdercoat, Eurosteel Signature finishes", kind: "Page", href: "/finishes" },
  { title: "Projects", subtitle: "Residential and commercial installations", kind: "Page", href: "/projects" },
  { title: "Showroom", subtitle: "Book a visit · 3 Byron Avenue, Takapuna", kind: "Page", href: "/showroom" },
  { title: "FAQ", subtitle: "Lead times, warranty, measuring, pricing", kind: "Page", href: "/faq" },
  { title: "Contact", subtitle: "Get in touch · orders@euro-steel.co.nz", kind: "Page", href: "/#enquire" },
  { title: "Order Status", subtitle: "Track an existing order by email", kind: "Page", href: "/order-status" },
];

const CATEGORIES = [
  { title: "Steel Windows", subtitle: "Casement, awning, fixed, arched, bay, picture", kind: "Category", href: "/products/steel-windows" },
  { title: "Steel Doors", subtitle: "French, pivot, bifold, sliding, arched, custom", kind: "Category", href: "/products/steel-doors" },
  { title: "Luxury Entrances", subtitle: "Bespoke one-of-one external pivot and hinge entrances", kind: "Category", href: "/products/luxury-entrances" },
  { title: "Hardware", subtitle: "Levers, pulls, locks, hinges, electronic hardware", kind: "Category", href: "/products/hardware" },
];

// Flattened product list — code + descriptive name. Mirrors the real-render
// slots in /pages/Products.jsx (REAL_PRODUCTS). Keep names in sync when
// adding/renaming products; search covers code and name.
const PRODUCTS = [
  // Steel Windows (SW1 – SW12)
  { code: "SW1", name: "4-Lite Casement Window" },
  { code: "SW2", name: "8-Lite Casement Window" },
  { code: "SW3", name: "16-Lite Casement Window" },
  { code: "SW4", name: "Arched-Top Fixed Steel Window" },
  { code: "SW5", name: "4-Lite Fixed Steel Window" },
  { code: "SW6", name: "Cathedral Arched Fixed Steel Window" },
  { code: "SW7", name: "Awning Steel Window" },
  { code: "SW8", name: "Triple-Section Steel Window with Double Awning" },
  { code: "SW9", name: "4-Lite Awning Steel Window" },
  { code: "SW10", name: "4-Panel Bi-Fold Steel Window" },
  { code: "SW11", name: "4-Panel Casement Steel Window with Transom" },
  { code: "SW12", name: "Arched 4-Panel Casement Steel Window" },
  // Steel Doors (SD1 – SD15)
  { code: "SD1", name: "3-Panel Single Steel Door" },
  { code: "SD2", name: "8-Lite Single Steel Door" },
  { code: "SD3", name: "Single Steel Door with Fixed Sidelight" },
  { code: "SD4", name: "8-Lite Double Steel Door" },
  { code: "SD5", name: "6-Lite Divider Double Steel Door" },
  { code: "SD6", name: "Double French Steel Door — 6-Lite" },
  { code: "SD7", name: "16-Lite Divider Double Steel Door" },
  { code: "SD8", name: "Single Steel Door with Twin Sidelights" },
  { code: "SD9", name: "Double French Steel Door with Transom" },
  { code: "SD10", name: "Full-Arch Double Steel Door — French" },
  { code: "SD11", name: "Full-Arch Double Steel Door" },
  { code: "SD12", name: "Arch-in-Square Double Steel Door" },
  { code: "SD13", name: "Tri-Panel Bi-Fold Steel Door" },
  { code: "SD14", name: "Double Steel Door with Sidelights & Transom" },
  { code: "SD15", name: "Full-Arch Double Steel Door with Sidelights" },
  // Luxury Entrances (LE1 – LE8)
  { code: "LE1", name: "Ribbed Double Pivot Entrance" },
  { code: "LE2", name: "Stone Panel Pivot Entrance" },
  { code: "LE3", name: "Bronze Patina Pivot Entrance" },
  { code: "LE4", name: "Arched Ribbed Double Entrance" },
  { code: "LE5", name: "Horizontal-Panel Double Pivot Entrance" },
  { code: "LE6", name: "Fluted Pivot Entrance with Sidelight" },
  { code: "LE7", name: "Fluted Pivot Entrance with Twin Sidelights" },
  { code: "LE8", name: "Horizontal-Panel Pivot Entrance with Twin Sidelights" },
];

const FINISHES = [
  { title: "Dulux Powdercoat", subtitle: "Standard architectural Dulux colours", kind: "Finish", href: "/finishes" },
  { title: "Eurosteel Signature Finishes", subtitle: "Flat T, Compact Pyramid, Slim Ridge, bespoke patinas", kind: "Finish", href: "/finishes" },
  { title: "Black", subtitle: "Matte black Dulux Powdercoat", kind: "Colour", href: "/finishes" },
  { title: "White", subtitle: "Matte white Dulux Powdercoat", kind: "Colour", href: "/finishes" },
  { title: "Bronze", subtitle: "Electro Dark Bronze finish", kind: "Colour", href: "/finishes" },
  { title: "Charcoal", subtitle: "Deep charcoal matte", kind: "Colour", href: "/finishes" },
];

const INDEX = [
  ...PAGES,
  ...CATEGORIES,
  ...PRODUCTS.map((p) => ({
    title: p.name,
    subtitle: `Model ${p.code}`,
    kind: "Product",
    code: p.code,
    // Deep-link to the right filtered grid; opening the modal from a URL is
    // out of scope, but landing on the right section is enough.
    href: p.code.startsWith("SW")
      ? "/products/steel-windows"
      : p.code.startsWith("SD")
        ? "/products/steel-doors"
        : p.code.startsWith("LE")
          ? "/products/luxury-entrances"
          : "/products/hardware",
  })),
  ...FINISHES,
];

function score(entry, q) {
  const Q = q.toLowerCase().trim();
  if (!Q) return 0;
  const title = (entry.title || "").toLowerCase();
  const sub = (entry.subtitle || "").toLowerCase();
  const code = (entry.code || "").toLowerCase();
  // Exact-ish code hit wins outright (e.g. "SD3")
  if (code && (code === Q || code.replace(/[-\s]/g, "") === Q.replace(/[-\s]/g, "")))
    return 100;
  if (title === Q) return 95;
  if (title.startsWith(Q)) return 80;
  if (title.includes(Q)) return 60;
  if (sub.includes(Q)) return 40;
  if (code && code.includes(Q)) return 55;
  // Token match (handles multi-word queries like "steel doors")
  const tokens = Q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    const hits = tokens.filter((t) => title.includes(t) || sub.includes(t)).length;
    if (hits === tokens.length) return 50;
    if (hits > 0) return 20;
  }
  return 0;
}

export function search(query, limit = 8) {
  const q = (query || "").trim();
  if (q.length < 1) return [];
  const scored = INDEX.map((e) => ({ e, s: score(e, q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.e);
  return scored;
}

export default search;
